import { HELP_MENU_ID, HELP_MENU_SUB_TAB_ID } from '../data-types/help/help-menu/helpMenuEnums';
import { beingResponsibleLinks } from '../data-types/help/help-sections/helpBeingResponsibleConstants';
import { blockingLinks } from '../data-types/help/help-sections/helpBlockingSoftwareConstants';
import { supportLinks } from '../data-types/help/help-sections/helpSupportConstants';
import { auth, HELP_KWIFF_EMAIL } from '../data-types/initialisation/initialisationConstants';
import { gamblingLinks } from '../data-types/links/linksConstants';
import { MainMenuItem, MenuAvailabilityProps } from '../data-types/main-menu/mainMenuInterfaces';
import { PROMOTIONS_INBOX, PROMOTIONS_TAB_ID } from '../data-types/profile/profile-menu/profileMenuConstants';
import { UserReportOption } from '../data-types/profile/profile-sections/feedback/feedbackEnums';
import {
  featuredCategories,
  featuredCategoryDetails,
  INVALID_SPORT_PRIORITY,
  sportCategories,
  sportCategoryDetails,
  SPORTS_PRIORITY_OFFSET,
} from '../data-types/sports/sportsConstants';
import { InPlaySubNavTab, SportNavTab, SubNavTab } from '../data-types/sports/sportsInterfaces';
import { VALIDATION_TYPE } from '../data-types/validation/validationEnums';
import { getSportsOrderByCountry } from '../international/internationalSports';
import {
  getEmailToUse,
  helpResponsibleGamblingWithDeeplinkInsteadOfSubMenu,
  isFeedbackForumEligible,
  isUserCountryBlockedFromSeeingWalletProfitAndLoss,
  isUserCountryEligibleForCloseAccount,
  isUserCountryEligibleForReferAFriend,
  isUserCountryEligibleForResponsibleGambling,
  isUserCountryEligibleForStayingInControl,
  isZendeskDisabled,
  shouldHideAccountDetailsFromProfile,
  shouldHideVerificationFromProfile,
} from '../international/internationalUtils';
import { PATHS } from '../routing-system/appPaths';
import browserHistory from '../routing-system/browserHistory';
import { setDesktopProfilePath } from '../state/actions/navigationActions';
import { store } from '../state/store';
import { isOfType } from '../validation/typeValidation';
import { generateListIdFromTitle } from './general';
import { canDisplayMyPromotions } from './myPromotions';
import { isInboxFeatureEnabled } from './xtremepush';

export const sortSportsMenuByPriority = (sportsbookMenu: MainMenuItem[]) => {
  const {
    userData: { userLocation },
  } = store.getState();

  if (userLocation) {
    const sportsInUserCountry = getSportsOrderByCountry(userLocation);

    if (sportsInUserCountry && sportsInUserCountry.length) {
      return sportsbookMenu
        .map(item => {
          const id = item.id || item.listId;

          if (id && typeof id === 'number') {
            const sportPriorityInCountry = sportsInUserCountry.findIndex(sportId => sportId === id) + 1;
            const isSportAvailableInCountry = sportPriorityInCountry >= 1;
            item.priority = isSportAvailableInCountry ? SPORTS_PRIORITY_OFFSET + sportPriorityInCountry : INVALID_SPORT_PRIORITY;
          }
          return item;
        })
        .filter(sport => sport.priority !== INVALID_SPORT_PRIORITY)
        .sort((item, nextItem) => item.priority - nextItem.priority);
    }
  }

  return sportsbookMenu;
};

export const createTabsAndSubTabsMenu = (sportsbookMenu: MainMenuItem[]) => {
  const tabs: SportNavTab[] = [];
  const subTabs: SubNavTab = {};
  const inPlaySubTabs: InPlaySubNavTab[] = [];

  // map tabs to either featured (string ids) or sports (number ids)
  sportsbookMenu.forEach(item => {
    const id = item.id || item.listId;
    const isSportCategory = id && isOfType(id, VALIDATION_TYPE.NUMBER) && item.enabled;
    const isFeaturedCategory = id && isOfType(id, VALIDATION_TYPE.STRING);

    if (isSportCategory) {
      const menuItem = sportCategoryDetails[Number(id)];

      if (menuItem) {
        if (item.empty) {
          menuItem.empty = true;
        }

        if (item.type === 'subNavigation' && item.subnavItems) {
          const isEleague = item.sportId === sportCategories.ELEAGUES;

          subTabs[item.id] = item.subnavItems.map(subnavItem => {
            subnavItem.link = isEleague
              ? String(subnavItem.sportId)
              : (subnavItem.listId && subnavItem.listId.toLowerCase()) || generateListIdFromTitle(subnavItem.title);

            if (subnavItem.subnavItems) {
              subnavItem.subnavItems = subnavItem.subnavItems.map(subsubnavItem => {
                subsubnavItem.parentLink = subnavItem.link;
                return subsubnavItem;
              });
            }

            if (
              (subnavItem.listId === featuredCategories.IN_PLAY && !subnavItem.empty) ||
              (subnavItem.listId === featuredCategories.IN_PLAY_24_HOURS && !subnavItem.emptyLiveEvents)
            ) {
              const inPlaySubItem: InPlaySubNavTab = {
                sportId: subnavItem.sportId,
                nameId: menuItem.nameId,
              };
              inPlaySubTabs.push(inPlaySubItem);
            }

            return subnavItem;
          });
        }
        tabs.push(menuItem);
      }
    }

    if (isFeaturedCategory) {
      const menuItem = featuredCategoryDetails[String(id)];
      if (menuItem) {
        if (item.empty) {
          menuItem.empty = true;
        }
        tabs.push(menuItem);
      }
    }
  });

  return { tabs, subTabs, inPlaySubTabs };
};

export const isMenuItemEligibleToRender = (data: MenuAvailabilityProps) => {
  switch (data.id) {
    case UserReportOption.FEEDBACK:
    case UserReportOption.REPORT_BUG:
    case UserReportOption.LEAVE_FEEDBACK:
    case UserReportOption.FEEDBACK_FORUM:
      return isFeedbackForumEligible(!!data.feedbackForum);
    case PROMOTIONS_TAB_ID:
      return canDisplayMyPromotions(data.authentication || auth.UNAUTHENTICATED, !!data.promoCentre);
    case PATHS.APP.PROFILE.PROFIT_AND_LOSS_HISTORY:
      return !isUserCountryBlockedFromSeeingWalletProfitAndLoss();
    case PROMOTIONS_INBOX:
      return isInboxFeatureEnabled();
    case PATHS.APP.PROFILE.REFER_A_FRIEND:
      return isUserCountryEligibleForReferAFriend() && data.referAFriend;
    case PATHS.APP.PROFILE.CONTROL:
    case HELP_MENU_SUB_TAB_ID.CONTROL:
      return isUserCountryEligibleForStayingInControl();
    case HELP_MENU_ID.RESPONSIBLE_GAMBLING:
      return isUserCountryEligibleForResponsibleGambling();
    case HELP_MENU_ID.RESPONSIBLE_GAMBLING_NON_EXPANDABLE:
      return !isUserCountryEligibleForResponsibleGambling() && helpResponsibleGamblingWithDeeplinkInsteadOfSubMenu();
    case PATHS.APP.PROFILE.CLOSE:
      return isUserCountryEligibleForCloseAccount();
    case PATHS.APP.PROFILE.DETAILS:
      return !shouldHideAccountDetailsFromProfile();
    case PATHS.APP.PROFILE.VERIFICATION.ROOT:
      return !shouldHideVerificationFromProfile();
    case PATHS.APP.PROFILE.VERIFICATION.EMPLOYMENT_DETAILS.ROOT:
      return data.pendingQuestions || false;
    case PATHS.APP.PROFILE.VERIFICATION.SAFER_GAMBLING.ROOT:
      return !!data.saferGamblingQuestionsSet;
    case HELP_MENU_ID.FAQS:
      return !isZendeskDisabled(data.userLocation);
    case HELP_MENU_ID.CHAT_TO_US:
      return !isZendeskDisabled(data.userLocation);
    default:
      return true;
  }
};

export const openHelpContentLink = (link: string | undefined, isDesktopLayout?: boolean) => {
  switch (link) {
    case blockingLinks.gamblock:
      window.open(blockingLinks.gamblock, '_blank');
      break;
    case blockingLinks.gamban:
      window.open(blockingLinks.gamban, '_blank');
      break;
    case blockingLinks.netnanny:
      window.open(blockingLinks.netnanny, '_blank');
      break;
    case beingResponsibleLinks.selfAssessment:
      window.open(beingResponsibleLinks.selfAssessment, '_blank');
      break;
    case gamblingLinks.gamstop:
      window.open(gamblingLinks.gamstop, '_blank');
      break;
    case HELP_KWIFF_EMAIL: {
      window.open(`mailto:${getEmailToUse()}`, '_blank');
      break;
    }
    case PATHS.APP.PROFILE.CONTROL:
      if (isDesktopLayout) {
        setDesktopProfilePath(PATHS.APP.PROFILE.CONTROL);
        browserHistory.push(PATHS.APP.SPORTS.DEFAULT);
      } else {
        browserHistory.push(link);
      }
      break;
    case supportLinks.gambleAware:
      window.open(supportLinks.gambleAware, '_blank');
      break;
    case supportLinks.gamcare:
      window.open(supportLinks.gamcare, '_blank');
      break;
    case supportLinks.gamblersAnonymous:
      window.open(supportLinks.gamblersAnonymous, '_blank');
      break;
    case supportLinks.gamblingTherapy:
      window.open(supportLinks.gamblingTherapy, '_blank');
      break;
    default:
      break;
  }
};
